/**
 * The space an ancestry's voice rings in, laid over the shared hero voice packs in the browser; the recordings stay untouched and
 * nothing here changes timing or outcomes. An ancestry not listed speaks dry.
 */
export interface VoiceSpace {
  /**
   * Gain on the room's return. The room's impulse is normalised to unit energy, which spreads it thin over a long tail, so the return
   * is quiet: at .5 the tail just after a line sits about 40 dB under the line, a hint of space rather than an echo.
   */
  reverb: number;
  /** Length of the tail, in seconds. */
  seconds: number;
  /** Seconds before the reflections arrive, which keeps the words clear of their own tail. */
  predelay: number;
  /** The tail is darkened above this frequency (Hz), as a real hall absorbs the highs. */
  tone: number;
}

export const ancestryVoiceSpaces: Readonly<Record<string, VoiceSpace>> = {
  // A natural hall rather than an effect: a short pre-delay and a soft, darkened tail behind every bark.
  celestial: { reverb: .5, seconds: 1.8, predelay: .03, tone: 6000 },
};

/** How a line is changed for one kind of moment, whoever speaks it: played at `rate` (below 1 is lower and slower) in its own room. */
export interface VoiceTreatment { rate: number; space: VoiceSpace }

/** The Shadowmancer's powers: the voice drops about two semitones and rings out in a dark hall, as if spoken from the shadows. */
export const shadowVoice: VoiceTreatment = { rate: .88, space: { reverb: 2, seconds: 2.4, predelay: .04, tone: 3500 } };

export const voiceSpaceFor = (ancestryId: string | undefined): VoiceSpace | undefined => ancestryId ? ancestryVoiceSpaces[ancestryId] : undefined;
